/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import metrics from '@adobe/exc-app/metrics';
import {query as queryApi} from '@adobe/exc-app/network';
import {setShellInitDataForLogin, setShellInitDataForReload} from './UnifiedShellInit';

const GQL_FRAGMENTS = {
  imsSSOhandler: `
    fragment imsSSOhandlerFragment on ImsSSOhandlerResponse {
      redirectLocation
      message
      currentImsOrgId
      currentAnalyticsPC
    }`,
  userOrgs: `
    fragment userOrgsFragment on ImsOrganization {
      orgName
      orgRef {
        ident
        authSrc
      }
      countryCode
      orgType
    }`,
  userOrgSettings: `
    fragment userOrgSettingsFragment on CustomPreferencesResponse {
      level
      appId
      groupId
      userId
      orgId
      settings
    }`};

export const RELOAD_QUERY = `
  query shellInitDataQuery($locale: String, $userId: String!, $apiKey: String!, $userCurrentOrgId: String, $sessionUrl: String, $config_version: Int, $keys: [String]!) {
    shellInitData(sessionUrl: $sessionUrl) {
      userOrgs {
        ...userOrgsFragment
      }
      imsSessionData
      userBehance(userId: $userId, apiKey: $apiKey) {
        images
        encodedImage(imageKey:[100,115])
      }
      userOrgSettings(keys: $keys, userCurrentOrgId: $userCurrentOrgId, isLoggedIn: true ) {
        ...userOrgSettingsFragment
      }
      timeline(config_version: $config_version, userCurrentOrgId: $userCurrentOrgId, isLoggedIn: true, locale: $locale)
    }
    userProfileJson
  }
  ${GQL_FRAGMENTS.userOrgs}
  ${GQL_FRAGMENTS.userOrgSettings}
`;

export const LOGIN_QUERY = `
  query shellInitDataQuery($locale: String, $expiryTimeInterval: String, $redirectLocation: String, $userId: String!, $apiKey: String!, $config_version: Int, $keys: [String]!) {
    shellInitData(expiryTimeInterval: $expiryTimeInterval, redirectLocation: $redirectLocation) {
      userOrgs {
        ...userOrgsFragment
      }
      imsSSOhandler {
        ...imsSSOhandlerFragment
      }
      userBehance(userId: $userId, apiKey: $apiKey) {
        images
        encodedImage(imageKey:[100,115])
      }
      userOrgSettings(keys: $keys, isLoggedIn: false) {
        ...userOrgSettingsFragment
      }
      timeline(config_version: $config_version, isLoggedIn: false, locale: $locale)
    }
  }
  ${GQL_FRAGMENTS.userOrgs}
  ${GQL_FRAGMENTS.imsSSOhandler}
  ${GQL_FRAGMENTS.userOrgSettings}
`;

const REQ_ERR_MSG = '[GraphQL Service Error in Shell Data Init]';

export class GraphQlService {
  constructor() {
    this.metrics = metrics.create('exc.core.GraphQlService');
  }

  initializeShellDataForLogin(variables) {
    return this.shellDataHandler(LOGIN_QUERY, {
      debugKey: 'User Login',
      store: setShellInitDataForLogin
    })(variables);
  }

  initializeShellDataForReload(variables) {
    return this.shellDataHandler(RELOAD_QUERY, {
      debugKey: 'App Re-load',
      store: setShellInitDataForReload
    })(variables);
  }

  async request(query, variables) {
    let res = await queryApi({data: {operationName: this.getOperationName(query), query, variables}});
    if (res && !res.ok)  {
      let reqId = res && res.headers && res.headers.get('x-request-id');
      let e = new Error(`Non-2xx response from gql. ReqID: ${reqId}`);
      e.status = res.status;
      throw e;
    }

    const json = await res.json() || {};
    json.reqId =  res && res.headers && res.headers.get('x-request-id');

    // Handling inconsistent 401s from the gql response layer
    // If we get > 1, we want to throw an error. This is due to adobe.io caching.
    const hasUnauthorizedErrors = json.errors && json.errors.map(err => {
      this.metrics.error(`${REQ_ERR_MSG} Message: ${err.message}`,
        err, {requestId: json.reqId});
      return ((err.extensions || {}).response || {}).status;
    }).some(s => s === 401);

    if (hasUnauthorizedErrors) {
      let e = new Error(`Non-2xx response from backing services in gql. ReqID: ${json.reqId}`);
      e.status = 401;
      throw e;
    }

    return json;
  }
  shellDataHandler(query, {debugKey, store}) {
    return async (variables) => {
      try {
        const response = await this.request(query, variables);
        const data = response.data;
        if (data) {
          // set the response in session storage
          store(data, variables);
          this.metrics.info(`Initialized Shell Data required for ${debugKey} using GQL.`,
            {requestId: response.reqID});
        }
        return data;
      } catch (error) {
        this.metrics.error(`Failed to initialize Shell App Data for ${debugKey}`);
        throw error;
      }
    };
  }

  getOperationName(query) {
    const name = /(query|mutation) ?([\w\d-_]+)? ?\(.*?\)? \{/.exec(query);
    return (name && name.length) ? name[2] : undefined;
  }
}
